/**
 * Page de configuration LLM - Interface Dev/Super Admin
 * Configuration des modèles IA et paramètres d'inférence
 */

import { useState } from 'react';

interface LLMConfig {
  classifierModel: string;
  structurerModel: string;
  contextWindow: number;
  threads: number;
  temperature: number;
  maxTokens: number;
}

interface ServiceStatus {
  name: string;
  status: 'running' | 'stopped' | 'error';
  uptime: string;
  lastCheck: string;
}

export function LLMConfigPage() {
  const [config, setConfig] = useState<LLMConfig>({
    classifierModel: 'qwen3-0.6b-q4.gguf',
    structurerModel: 'qwen3-4b-q4.gguf',
    contextWindow: 2048,
    threads: 4,
    temperature: 0.7,
    maxTokens: 512,
  });

  const [services] = useState<ServiceStatus[]>([
    { name: 'ASR Service (Voxtral)', status: 'running', uptime: '2h 34m', lastCheck: 'Il y a 5s' },
    { name: 'LLM Classifier (Qwen3-0.6B)', status: 'running', uptime: '2h 34m', lastCheck: 'Il y a 3s' },
    { name: 'LLM Structurer (Qwen3-4B)', status: 'running', uptime: '2h 34m', lastCheck: 'Il y a 4s' },
    { name: 'Wake Word Engine', status: 'stopped', uptime: '-', lastCheck: 'Il y a 1m' },
    { name: 'PostgreSQL Database', status: 'running', uptime: '5h 12m', lastCheck: 'Il y a 2s' },
    { name: 'Valkey Cache', status: 'running', uptime: '5h 12m', lastCheck: 'Il y a 6s' },
  ]);

  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const handleSave = async () => {
    setIsSaving(true);
    setSuccessMessage('');

    // Simuler une sauvegarde
    await new Promise((resolve) => setTimeout(resolve, 1000));

    setIsSaving(false);
    setSuccessMessage('Configuration sauvegardée avec succès !');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleReset = () => {
    setConfig({
      classifierModel: 'qwen3-0.6b-q4.gguf',
      structurerModel: 'qwen3-4b-q4.gguf',
      contextWindow: 2048,
      threads: 4,
      temperature: 0.7,
      maxTokens: 512,
    });
  };

  const getStatusBadge = (status: ServiceStatus['status']) => {
    switch (status) {
      case 'running':
        return 'bg-success';
      case 'stopped':
        return 'bg-secondary';
      case 'error':
        return 'bg-danger';
    }
  };

  const getStatusIcon = (status: ServiceStatus['status']) => {
    switch (status) {
      case 'running':
        return 'bi-check-circle-fill';
      case 'stopped':
        return 'bi-dash-circle-fill';
      case 'error':
        return 'bi-exclamation-triangle-fill';
    }
  };

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <h1 className="text-gradient fw-bold mb-2">
            <i className="bi bi-cpu me-2"></i>
            Configuration LLM
          </h1>
          <p className="text-muted">
            <i className="bi bi-shield-lock me-1"></i>
            Interface Dev/Super Admin - Gestion des modèles IA et paramètres d'inférence
          </p>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-success d-flex align-items-center fade-in" role="alert">
              <i className="bi bi-check-circle-fill me-2"></i>
              <div>{successMessage}</div>
            </div>
          </div>
        </div>
      )}

      <div className="row g-4">
        {/* Services Status */}
        <div className="col-12 col-lg-6">
          <div className="card card-premium bounce-in">
            <div className="card-header bg-gradient-primary text-white">
              <i className="bi bi-server me-2"></i>
              <strong>État des Services</strong>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Service</th>
                      <th>État</th>
                      <th>Uptime</th>
                      <th>Dernière vérif.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {services.map((service, idx) => (
                      <tr key={idx}>
                        <td>
                          <span className="fw-semibold">{service.name}</span>
                        </td>
                        <td>
                          <span className={`badge ${getStatusBadge(service.status)}`}>
                            <i className={`bi ${getStatusIcon(service.status)} me-1`}></i>
                            {service.status}
                          </span>
                        </td>
                        <td className="text-muted">{service.uptime}</td>
                        <td className="text-muted small">{service.lastCheck}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="card-footer bg-gradient-light d-flex justify-content-between align-items-center">
              <small className="text-muted">
                <i className="bi bi-clock me-1"></i>
                Rafraîchissement auto: 10s
              </small>
              <button className="btn btn-sm btn-outline-primary">
                <i className="bi bi-arrow-clockwise me-1"></i>
                Actualiser
              </button>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="col-12 col-lg-6">
          <div className="row g-3">
            <div className="col-6">
              <div className="card text-center bounce-in" style={{ animationDelay: '0.1s' }}>
                <div className="card-body">
                  <i className="bi bi-speedometer2 text-primary" style={{ fontSize: '2rem' }}></i>
                  <h3 className="mt-2 mb-0 fw-bold">2.8s</h3>
                  <p className="text-muted small mb-0">Latence moyenne</p>
                </div>
              </div>
            </div>
            <div className="col-6">
              <div className="card text-center bounce-in" style={{ animationDelay: '0.2s' }}>
                <div className="card-body">
                  <i className="bi bi-graph-up text-success" style={{ fontSize: '2rem' }}></i>
                  <h3 className="mt-2 mb-0 fw-bold">94%</h3>
                  <p className="text-muted small mb-0">Précision ASR</p>
                </div>
              </div>
            </div>
            <div className="col-6">
              <div className="card text-center bounce-in" style={{ animationDelay: '0.3s' }}>
                <div className="card-body">
                  <i className="bi bi-cpu text-info" style={{ fontSize: '2rem' }}></i>
                  <h3 className="mt-2 mb-0 fw-bold">42%</h3>
                  <p className="text-muted small mb-0">Utilisation CPU</p>
                </div>
              </div>
            </div>
            <div className="col-6">
              <div className="card text-center bounce-in" style={{ animationDelay: '0.4s' }}>
                <div className="card-body">
                  <i className="bi bi-memory text-warning" style={{ fontSize: '2rem' }}></i>
                  <h3 className="mt-2 mb-0 fw-bold">1.2GB</h3>
                  <p className="text-muted small mb-0">RAM utilisée</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Model Configuration */}
        <div className="col-12 col-lg-6">
          <div className="card card-premium fade-in">
            <div className="card-header" style={{ background: '#673AB7', color: 'white' }}>
              <i className="bi bi-braces me-2"></i>
              <strong>Modèles LLM</strong>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  <i className="bi bi-tags me-1"></i>
                  Modèle de classification
                </label>
                <select
                  className="form-select"
                  value={config.classifierModel}
                  onChange={(e) => setConfig({ ...config, classifierModel: e.target.value })}
                >
                  <option value="qwen3-0.6b-q4.gguf">Qwen3-0.6B Q4 (Rapide)</option>
                  <option value="qwen3-1.5b-q4.gguf">Qwen3-1.5B Q4 (Équilibré)</option>
                  <option value="mistral-7b-q4.gguf">Mistral-7B Q4 (Précis)</option>
                </select>
                <small className="text-muted">
                  <i className="bi bi-info-circle me-1"></i>
                  Modèle pour classifier les types d'actes (traçabilité, DAR, etc.)
                </small>
              </div>

              <div className="mb-0">
                <label className="form-label fw-semibold">
                  <i className="bi bi-diagram-3 me-1"></i>
                  Modèle de structuration
                </label>
                <select
                  className="form-select"
                  value={config.structurerModel}
                  onChange={(e) => setConfig({ ...config, structurerModel: e.target.value })}
                >
                  <option value="qwen3-4b-q4.gguf">Qwen3-4B Q4 (Recommandé)</option>
                  <option value="qwen3-7b-q4.gguf">Qwen3-7B Q4 (Haute précision)</option>
                  <option value="mistral-7b-q4.gguf">Mistral-7B Q4 (Alternatif)</option>
                </select>
                <small className="text-muted">
                  <i className="bi bi-info-circle me-1"></i>
                  Modèle pour extraire les données structurées (médicament, dose, etc.)
                </small>
              </div>
            </div>
          </div>
        </div>

        {/* Inference Parameters */}
        <div className="col-12 col-lg-6">
          <div className="card card-premium fade-in">
            <div className="card-header" style={{ background: '#FF5722', color: 'white' }}>
              <i className="bi bi-sliders me-2"></i>
              <strong>Paramètres d'Inférence</strong>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    <i className="bi bi-layers me-1"></i>
                    Context Window
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={config.contextWindow}
                    onChange={(e) => setConfig({ ...config, contextWindow: parseInt(e.target.value) })}
                    min={512}
                    max={4096}
                    step={512}
                  />
                  <small className="text-muted">Tokens (512-4096)</small>
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    <i className="bi bi-cpu me-1"></i>
                    CPU Threads
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={config.threads}
                    onChange={(e) => setConfig({ ...config, threads: parseInt(e.target.value) })}
                    min={1}
                    max={16}
                  />
                  <small className="text-muted">Threads (1-16)</small>
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    <i className="bi bi-thermometer-half me-1"></i>
                    Temperature
                  </label>
                  <input
                    type="range"
                    className="form-range"
                    value={config.temperature}
                    onChange={(e) => setConfig({ ...config, temperature: parseFloat(e.target.value) })}
                    min={0}
                    max={1}
                    step={0.1}
                  />
                  <small className="text-muted">
                    Valeur: <strong>{config.temperature}</strong> (0=déterministe, 1=créatif)
                  </small>
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    <i className="bi bi-textarea me-1"></i>
                    Max Tokens
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={config.maxTokens}
                    onChange={(e) => setConfig({ ...config, maxTokens: parseInt(e.target.value) })}
                    min={128}
                    max={2048}
                    step={128}
                  />
                  <small className="text-muted">Tokens (128-2048)</small>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="mb-1 fw-semibold">
                    <i className="bi bi-wrench me-2"></i>
                    Actions de configuration
                  </h6>
                  <small className="text-muted">
                    Les modifications nécessiteront un redémarrage des services LLM
                  </small>
                </div>
                <div className="d-flex gap-2">
                  <button onClick={handleReset} className="btn btn-outline-secondary">
                    <i className="bi bi-arrow-counterclockwise me-2"></i>
                    Réinitialiser
                  </button>
                  <button onClick={handleSave} disabled={isSaving} className="btn btn-primary shadow-glow">
                    {isSaving ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Sauvegarde...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-2"></i>
                        Sauvegarder la configuration
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Panel */}
        <div className="col-12">
          <div className="alert alert-info d-flex align-items-start">
            <i className="bi bi-lightbulb me-2 mt-1"></i>
            <div>
              <strong>Interface Dev/Super Admin :</strong> Cette interface permet de configurer les modèles
              IA locaux (llama-cpp-python) et leurs paramètres d'inférence. Les changements nécessitent
              un redémarrage des services concernés. Pour le MVP, les modèles Qwen3 Q4 offrent le meilleur
              compromis vitesse/précision sur CPU.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
